import React, { useRef, useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Highlighter, Cloud, CloudOff, Type } from 'lucide-react';
import { Button } from './button';
import { useHotkeys } from 'react-hotkeys-hook';
import DOMPurify from 'dompurify';

interface HighlightEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  savedStatus?: boolean;
  disabled?: boolean;
  minHeight?: string;
}

export function HighlightEditor({
  value = '',
  onChange,
  placeholder = 'Enter your text...',
  className,
  savedStatus = false,
  disabled = false,
  minHeight = '120px',
}: HighlightEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [hasHighlights, setHasHighlights] = useState(false);

  // Parse and sanitize the value to handle both plain text and HTML with marks
  const parseContent = (content: string): string => {
    // Sanitize HTML to prevent XSS attacks
    // Allow <mark> tags for highlighting and <br>/<div> for line breaks
    const sanitized = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['mark', 'br', 'div'],
      ALLOWED_ATTR: ['class'],
      KEEP_CONTENT: true,
    });
    return sanitized;
  };

  // Initialize content
  useEffect(() => {
    if (editorRef.current && !isFocused) {
      const parsedContent = parseContent(value);
      if (editorRef.current.innerHTML !== parsedContent) {
        editorRef.current.innerHTML = parsedContent;
        updateCharCount();
        checkForHighlights();
      }
    }
  }, [value, isFocused]);

  // Update character count
  const updateCharCount = () => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || '';
      setCharCount(text.length);
    }
  };

  // Check if content has highlights
  const checkForHighlights = () => {
    if (editorRef.current) {
      const marks = editorRef.current.querySelectorAll('mark');
      setHasHighlights(marks.length > 0);
    }
  };

  // Handle content change
  const handleInput = () => {
    if (editorRef.current && onChange) {
      const html = editorRef.current.innerHTML;
      onChange(html);
      updateCharCount();
      checkForHighlights();
    }
  };

  // Highlight selected text
  const highlightSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    if (!selectedText) return;

    // Check if we're inside the editor
    if (!editorRef.current?.contains(range.commonAncestorContainer)) return;

    // Create a mark element
    const mark = document.createElement('mark');
    mark.className = 'highlight-mark';
    
    try {
      // Check if selection is already highlighted
      const parentMark = range.commonAncestorContainer.parentElement?.closest('mark');
      if (parentMark) {
        // Unhighlight if already highlighted
        const parent = parentMark.parentNode;
        while (parentMark.firstChild) {
          parent?.insertBefore(parentMark.firstChild, parentMark);
        }
        parent?.removeChild(parentMark);
      } else {
        // Highlight the selection
        range.surroundContents(mark);
      }
      
      // Clear selection
      selection.removeAllRanges();
      
      // Trigger change event
      handleInput();
      
      // Flash animation
      setIsHighlighting(true);
      setTimeout(() => setIsHighlighting(false), 200);
    } catch (e) {
      // If surroundContents fails (e.g., selection spans multiple elements),
      // extract and wrap the content
      const contents = range.extractContents();
      mark.appendChild(contents);
      range.insertNode(mark);
      selection.removeAllRanges();
      handleInput();
      
      setIsHighlighting(true);
      setTimeout(() => setIsHighlighting(false), 200);
    }
  }, [handleInput]);

  // Clear all highlights
  const clearHighlights = useCallback(() => {
    if (!editorRef.current) return;
    
    const marks = editorRef.current.querySelectorAll('mark');
    marks.forEach(mark => {
      const parent = mark.parentNode;
      while (mark.firstChild) {
        parent?.insertBefore(mark.firstChild, mark);
      }
      parent?.removeChild(mark);
    });
    
    handleInput();
  }, [handleInput]);

  // Keyboard shortcuts
  useHotkeys('cmd+h, ctrl+h', (e) => {
    e.preventDefault();
    highlightSelection();
  }, {
    enableOnContentEditable: true,
  });

  useHotkeys('cmd+shift+h, ctrl+shift+h', (e) => {
    e.preventDefault();
    clearHighlights();
  }, {
    enableOnContentEditable: true,
  });

  // Handle paste to strip formatting except marks
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    
    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(text));
    selection.collapseToEnd();
    handleInput();
  };

  return (
    <div className={cn('highlight-editor-container', className)}>
      {/* Toolbar */}
      <div className="editor-toolbar">
        <div className="toolbar-left">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={highlightSelection}
            onMouseDown={(e) => e.preventDefault()}
            disabled={disabled}
            className={cn(
              'toolbar-button',
              isHighlighting && 'highlighting'
            )}
            title="Highlight selected text (Cmd/Ctrl+H)"
          >
            <Highlighter className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Highlight</span>
          </Button>
          
          {hasHighlights && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearHighlights}
              onMouseDown={(e) => e.preventDefault()}
              disabled={disabled}
              className="toolbar-button text-muted-foreground"
              title="Clear all highlights (Cmd/Ctrl+Shift+H)"
            >
              <Type className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Clear</span>
            </Button>
          )}
        </div>
        
        <div className="toolbar-right">
          <span className="char-count">{charCount} characters</span>
          <div className="save-status" title={savedStatus ? 'Saved to server' : 'Not saved'}>
            {savedStatus ? (
              <Cloud className="w-4 h-4 text-green-600" />
            ) : (
              <CloudOff className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        className={cn(
          'editor-content',
          isFocused && 'focused',
          disabled && 'disabled'
        )}
        style={{ minHeight }}
        onInput={handleInput}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />

      {/* Styles */}
      <style>{`
        .highlight-editor-container {
          border: 1px solid hsl(var(--border));
          border-radius: 0.5rem;
          background: hsl(var(--background));
          overflow: hidden;
          transition: all 0.2s ease;
        }

        .highlight-editor-container:focus-within {
          border-color: hsl(var(--ring));
          box-shadow: 0 0 0 3px hsl(var(--ring) / 0.1);
        }

        .editor-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem;
          border-bottom: 1px solid hsl(var(--border));
          background: hsl(var(--muted) / 0.3);
        }

        .toolbar-left {
          display: flex;
          gap: 0.25rem;
        }

        .toolbar-right {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .toolbar-button {
          transition: all 0.2s ease;
        }

        .toolbar-button.highlighting {
          background: hsl(var(--primary) / 0.1);
          color: hsl(var(--primary));
        }

        .char-count {
          font-size: 0.75rem;
          color: hsl(var(--muted-foreground));
        }

        .save-status {
          display: flex;
          align-items: center;
        }

        .editor-content {
          padding: 0.75rem;
          font-size: 0.875rem;
          line-height: 1.5rem;
          outline: none;
          word-wrap: break-word;
          white-space: pre-wrap;
        }

        .editor-content.focused {
          background: hsl(var(--background));
        }

        .editor-content.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: hsl(var(--muted) / 0.1);
        }

        .editor-content:empty:before {
          content: attr(data-placeholder);
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          position: absolute;
        }

        .editor-content mark,
        .editor-content .highlight-mark {
          background: #fef3c7;
          color: inherit;
          padding: 0.125rem 0;
          border-radius: 0.125rem;
          transition: background 0.2s ease;
        }

        .editor-content mark:hover,
        .editor-content .highlight-mark:hover {
          background: #fde68a;
        }

        /* Animation for new highlights */
        @keyframes highlight-flash {
          0% { background: #fde68a; }
          100% { background: #fef3c7; }
        }

        .editor-content mark.new-highlight {
          animation: highlight-flash 0.3s ease;
        }

        /* Dark mode support */
        .dark .editor-content mark,
        .dark .editor-content .highlight-mark {
          background: rgba(251, 191, 36, 0.3);
          color: inherit;
        }

        .dark .editor-content mark:hover,
        .dark .editor-content .highlight-mark:hover {
          background: rgba(251, 191, 36, 0.4);
        }

        /* Mobile responsiveness */
        @media (max-width: 640px) {
          .editor-toolbar {
            padding: 0.375rem;
          }
          
          .editor-content {
            padding: 0.625rem;
            font-size: 0.875rem;
          }
        }
      `}</style>
    </div>
  );
}
